import { Config } from 'config/config.interface'
import { FieldState } from 'final-form'
import { Data } from 'interfaces/ApolloResponse.interface'
import { ALL_RULES } from './validation.rules'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ValidationFunction<T = any> = (
  value: T,
  allValues: Data,
  config: Config,
  meta?: FieldState<unknown>
) => string | null | undefined

/**
 * Returns the rule(s) for the given path.
 *
 * @param {string} path The path, e.g. `debtors.primary.expenses.rent`
 */
export const getRulesForPath = (
  path: string,
  rules = ALL_RULES
): ValidationFunction[] => {
  // If the path matches exactly one path in the rules, we can simply return the rules.
  if (rules[path]) return rules[path]
  // In the rules object above, one can specify wildcard paths, e.g. debtors.*.whatever.
  // When this method is called, the actual path will be passed in, e.g. debtors.primary.whatever.
  // The wildcard path should match to both debtors.primary.whatever and debtor.secondary.whatever.
  // So, we first create a "mapping" of the paths in the rule object and the regex form of that path to
  // make it easier to test for the string match.
  const rulePaths = Object.keys(rules)
  // The mapping will contain arrays of declared shape, where the first element of the array...
  const wildcardRulesRegexMap: [RegExp, string][] = []
  // ... is merely the "regexed" form of the path/key...
  rulePaths
    .filter((p) => p.includes('*'))
    .forEach((p) => {
      const replacedPath = p
        .replace('.', '\\.')
        .replace(/\*/g, '.+')
        .replace('[', '\\[')
        .replace(']', '\\]')

      // ( resulting in, e.g. /^debtors\..*\.path$/ )
      const re = new RegExp(`^${replacedPath}$`, 'g')

      wildcardRulesRegexMap.push([re, p])
    })
  // ... so that it is possible to go through them and see if one matches.
  // In the case that it matches, the rules are collected in the "result"s array...
  const result = wildcardRulesRegexMap.map((entry) => {
    const [re, p] = entry

    if (re.test(path)) {
      return rules[p]
    }

    return []
  })

  // ... and, finally, merged in one array to be returned.
  return result.reduce((all, entry) => all.concat(entry), [])
}
